import React from 'react';
import { PlusIcon, StarIcon, Trash2Icon, UserIcon } from 'lucide-react';
import { CustomerContact } from '../../types/pro';
import { emptyContact } from '../../utils/contacts';
interface ContactsEditorProps {
  contacts: CustomerContact[];
  onChange: (contacts: CustomerContact[]) => void;
  /** Shown when the list is empty. */
  emptyHint: string;
}
const field = 'w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
const label = 'mb-1 block text-xs font-medium text-stone-500';

/**
 * A customer can have several people attached. One is marked primary — that
 * is who quotes and invoices are addressed to — and the rest are just there
 * so you can reach the right person without digging through notes.
 */
export function ContactsEditor({
  contacts,
  onChange,
  emptyHint
}: ContactsEditorProps) {
  const set = (id: string, changes: Partial<CustomerContact>) => onChange(contacts.map(contact => contact.id === id ? {
    ...contact,
    ...changes
  } : contact));
  const makePrimary = (id: string) => onChange(contacts.map(contact => ({
    ...contact,
    primary: contact.id === id
  })));
  const remove = (id: string) => {
    const rest = contacts.filter(contact => contact.id !== id);
    // Never leave the list without someone to address things to.
    if (rest.length > 0 && !rest.some(contact => contact.primary)) {
      rest[0] = {
        ...rest[0],
        primary: true
      };
    }
    onChange(rest);
  };
  const add = () => onChange([...contacts, emptyContact(contacts.length === 0)]);
  return <section data-id="element-84" data-mp="2jwg8b7sfulv6">
      <div className="mb-2 flex items-center justify-between gap-3" data-id="element-85" data-mp="2jwzpune6f88o">
        <h3 className="text-sm font-semibold text-stone-900" data-id="element-86" data-mp="2ovb0v1g7josh">Kontaktpersoner</h3>
        <button type="button" onClick={add} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-50" data-id="element-87" data-mp="18hksvjlywgdk">
          <PlusIcon className="h-4 w-4" data-id="element-88" data-mp="3g2jahblnjc8p" />
          Tilføj person
        </button>
      </div>

      {contacts.length === 0 ? <button type="button" onClick={add} className="flex w-full items-center gap-3 rounded-lg border border-dashed border-stone-300 px-4 py-4 text-left transition-colors hover:border-brand-400 hover:bg-brand-50" data-id="element-89" data-mp="3b6uibtbyoxmf">
          <UserIcon className="h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-90" data-mp="2wgwp2yz6dhqd" />
          <span className="text-sm text-stone-500" data-id="element-91" data-mp="2sei70h3bym05">{emptyHint}</span>
        </button> : <ul className="space-y-3" data-id="element-92" data-mp="2nex6w3cfc7lu">
          {contacts.map((contact, index) => <li key={contact.id} className={`rounded-lg border p-3.5 ${contact.primary ? 'border-brand-200 bg-brand-50/40' : 'border-stone-200'}`} data-id="element-93" data-mp="2nh05vcvk1b2p" data-mpi={index}>
              <div className="mb-2.5 flex items-center gap-2" data-id="element-94" data-mp="gq0owhphvszf">
                <button type="button" onClick={() => makePrimary(contact.id)} aria-pressed={contact.primary} title={contact.primary ? 'Primær kontakt' : 'Gør til primær kontakt'} className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold transition-colors ${contact.primary ? 'bg-brand-600 text-white' : 'text-stone-500 hover:bg-stone-100'}`} data-id="element-95" data-mp="o8i67a3m4c2y">
                  <StarIcon className="h-3.5 w-3.5" fill={contact.primary ? 'currentColor' : 'none'} data-id="element-96" data-mp="f5z5ownaubjv" />
                  {contact.primary ? 'Primær' : 'Gør primær'}
                </button>
                <span className="flex-1 text-xs text-stone-400" data-id="element-97" data-mp="1vljvhjxikvyn">
                  Person {index + 1}
                </span>
                <button type="button" onClick={() => remove(contact.id)} aria-label={`Fjern ${contact.name || `person ${index + 1}`}`} className="rounded-lg p-1.5 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-98" data-mp="1t86ksqliq7mq">
                  <Trash2Icon className="h-4 w-4" data-id="element-99" data-mp="1y4nahp8d8u1x" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-100" data-mp="2c4g8m5m82cjx">
                <label className="block" data-id="element-101" data-mp="2857dawbja6m9">
                  <span className={label} data-id="element-102" data-mp="282bfr93lljyc">Navn</span>
                  <input value={contact.name} onChange={e => set(contact.id, {
              name: e.target.value
            })} placeholder="f.eks. Lene Bech" className={field} data-id="element-103" data-mp="2lw2fxqvfvrhe" />
                </label>
                <label className="block" data-id="element-104" data-mp="2qv5jt7zonrns">
                  <span className={label} data-id="element-105" data-mp="2qw6u3a8vka4g">Rolle</span>
                  <input value={contact.role} onChange={e => set(contact.id, {
              role: e.target.value
            })} placeholder="Ejer, driftsleder, ægtefælle…" className={field} data-id="element-106" data-mp="3agyrbx54hwix" />
                </label>
                <label className="block" data-id="element-107" data-mp="35hm3b92imjer">
                  <span className={label} data-id="element-108" data-mp="35gjc101a6eyb">Telefon</span>
                  <input value={contact.phone} onChange={e => set(contact.id, {
              phone: e.target.value
            })} inputMode="tel" className={field} data-id="element-109" data-mp="2qsq1htr1y2ml" />
                </label>
                <label className="block" data-id="element-110" data-mp="2htusyb0jcgg4">
                  <span className={label} data-id="element-111" data-mp="2mquymkth3h6p">E-mail</span>
                  <input value={contact.email} onChange={e => set(contact.id, {
              email: e.target.value
            })} inputMode="email" className={field} data-id="element-112" data-mp="2mrcsxbwkrlbz" />
                </label>
                <label className="block sm:col-span-2" data-id="element-113" data-mp="1tok715nhdagt">
                  <span className={label} data-id="element-114" data-mp="1tq516u5vure5">Note</span>
                  <input value={contact.notes} onChange={e => set(contact.id, {
              notes: e.target.value
            })} placeholder="f.eks. træffes bedst efter kl. 15" className={field} data-id="element-115" data-mp="1owto4bah9eds" />
                </label>
              </div>
            </li>)}
        </ul>}

      {contacts.length > 1 && <p className="mt-2 text-xs text-stone-400" data-id="element-116" data-mp="27peenm2pf7ef">
          Den primære kontakt bruges automatisk på tilbud, fakturaer og mails.
        </p>}
    </section>;
}